import { Box, Modal, Text } from '@tocktix/pantry';
import { FunctionComponent, useState } from 'react';
import { useActions } from 'utils/connect';
import { userClient } from 'tools/bff/client';

interface RetryGdaEligibilityModalProps {
  uuid: string;
  open: boolean;
  onClose: () => void;
  onSubmit: () => Promise<void>;
}

const RetryGdaEligibilityModal: FunctionComponent<RetryGdaEligibilityModalProps> = ({
  uuid,
  open,
  onClose,
  onSubmit,
}) => {
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { actions } = useActions();

  const handleClose = () => {
    setError(null);
    onClose();
  };

  const handleSubmission = async () => {
    setError(null);
    setIsSubmitting(true);
    try {
      const response = await userClient.retryGdaEligibility({ gdusUuid: uuid });
      await onSubmit();
      handleClose();
      if (response.noAmexCard) {
        actions.toast.showInfo('No Amex card on file - nothing to retry');
      } else if (response.retriedCount > 0) {
        actions.toast.showInfo(
          `Retried ${response.retriedCount} eligibility check${
            response.retriedCount === 1 ? '' : 's'
          }`
        );
      } else {
        actions.toast.showInfo(
          response.message || 'Global Dining Access eligibility recheck requested'
        );
      }
    } catch (reason) {
      setError('Error retrying GDA eligibility: ' + reason);
      actions.toast.showError('Error retrying GDA eligibility');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal
      id="retry-gda-eligibility-modal"
      open={open}
      onClose={handleClose}
      size="md"
      data-testid="retry-gda-eligibility-modal"
    >
      <Modal.Header title="Retry GDA eligibility" onClose={handleClose} border />
      <Modal.Body>
        <Box display="flex" flexDirection="column" gap={200}>
          <Text variant="body" color="fgSubtle">
            This asks Resy to recompute Global Dining Access eligibility for the user&apos;s
            American Express card(s). Resy processes the recheck asynchronously, so any status
            change may take some time to appear and is not reflected in Tock or GDUS immediately. It
            only has an effect if the user has an eligible American Express card on file in Resy.
          </Text>
          {error && (
            <Text variant="body" color="fgDangerBase">
              {error}
            </Text>
          )}
        </Box>
      </Modal.Body>
      <Modal.Footer
        actions={{
          primary: {
            label: isSubmitting ? 'Retrying...' : 'Retry eligibility',
            type: 'button',
            disabled: isSubmitting,
            onClick: handleSubmission,
          },
          cancel: {
            label: 'Cancel',
            onClick: handleClose,
          },
        }}
      />
    </Modal>
  );
};

export default RetryGdaEligibilityModal;
